/**
 * EF-CLIENT-ROLE-FOUNDATION-V1: Client Portal Context
 * 
 * Provides role-based access control for Client Portal pages.
 * 
 * Roles:
 * - admin: Full access (add contacts, send invites, manage users)
 * - viewer: Read-only access (view projects, shifts, documents)
 * 
 * Future: Can be extended with permissions object without refactoring
 */

import { createContext, useContext } from 'react';

// Role constants
export const CLIENT_ROLE = {
  ADMIN: 'admin',
  VIEWER: 'viewer',
};

// Context type
const ClientPortalContext = createContext({
  profile: null,
  isAdmin: false,
  isViewer: true,
  role: CLIENT_ROLE.VIEWER,
  canManageContacts: false,
  canSendOnboardingLinks: false,
  canManageUsers: false,
});

// Hook to use client portal context
export function useClientPortal() {
  const context = useContext(ClientPortalContext);
  if (!context) {
    throw new Error('useClientPortal must be used within a ClientPortalProvider');
  }
  return context;
}

// Helper to create context value from profile
export function createClientPortalContextValue(profile) {
  const role = profile?.role || CLIENT_ROLE.VIEWER;
  const isAdmin = role === CLIENT_ROLE.ADMIN;
  const isViewer = role === CLIENT_ROLE.VIEWER;
  
  return {
    profile,
    role,
    isAdmin,
    isViewer,
    // Admin-only capabilities (future-proofed for permissions expansion)
    canManageContacts: isAdmin,
    canSendOnboardingLinks: isAdmin,
    canManageUsers: isAdmin,
    // Future: canCreateProjects, canCreateShifts, etc.
  };
}

export default ClientPortalContext;
